import type { AgencyGraphJson } from '@saaga/shared/interfaces/agency.interface'

interface SerializedNodeData {
  label: string
  roleDescription: string
  systemPrompt: string
  model: string
  toolList: string[]
  temperature: number
  isEntryPoint: boolean
  imageUrl?: string
}

interface SerializedNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: SerializedNodeData
}

interface SerializedEdge {
  id: string
  source: string
  target: string
  label?: string
}

/**
 * Deterministic serialization of flow data for dirty-state comparison.
 * Extracts only interface-defined fields to ignore React Flow internal properties.
 */
export const serializeFlowData = (graphJson: AgencyGraphJson): string => {
  const nodeList: SerializedNode[] = graphJson.nodeList
    .map((node) => ({
      id: node.id,
      type: node.type,
      position: { x: node.position.x, y: node.position.y },
      data: {
        label: node.data.label,
        roleDescription: node.data.roleDescription,
        systemPrompt: node.data.systemPrompt,
        model: node.data.model,
        toolList: [...node.data.toolList].sort(),
        temperature: node.data.temperature,
        isEntryPoint: node.data.isEntryPoint,
        imageUrl: node.data.imageUrl,
      },
    }))
    .sort((a, b) => a.id.localeCompare(b.id))

  const edgeList: SerializedEdge[] = graphJson.edgeList
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.label,
    }))
    .sort((a, b) => a.id.localeCompare(b.id))

  return JSON.stringify({ nodeList, edgeList })
}
